import type { GuestQuestion, GuestQuestionAnswers } from './types';

const SUPPORTED_QUESTION_TYPES = new Set(['short_text', 'single_choice']);

export function getActiveGuestQuestions(questions?: GuestQuestion[]): GuestQuestion[] {
  if (!Array.isArray(questions)) return [];

  const seenQuestionIds = new Set<string>();

  return questions
    .filter((question) => {
      if (!question || question.active === false) return false;
      if (typeof question.id !== 'string' || !question.id.trim() || seenQuestionIds.has(question.id)) return false;
      if (typeof question.prompt !== 'string' || !question.prompt.trim()) return false;
      if (!SUPPORTED_QUESTION_TYPES.has(question.type)) return false;

      seenQuestionIds.add(question.id);
      return true;
    })
    .map((question) => {
      if (question.type !== 'single_choice') return question;

      const seenOptionIds = new Set<string>();
      const options = Array.isArray(question.options)
        ? question.options.filter((option) => {
          if (!option || typeof option.id !== 'string' || !option.id.trim() || seenOptionIds.has(option.id)) return false;
          if (typeof option.label !== 'string' || !option.label.trim()) return false;
          seenOptionIds.add(option.id);
          return true;
        })
        : [];

      return { ...question, options };
    })
    .sort((a, b) => (a.order ?? 0) - (b.order ?? 0));
}

export function getActiveGuestQuestionOptions(question: GuestQuestion) {
  return (question.options || []).filter((option) => option.active !== false);
}

export function normalizeEventQuestionAnswers(
  value?: unknown
): GuestQuestionAnswers {
  const candidates = Array.isArray(value) ? value : [value];

  return candidates.reduce<GuestQuestionAnswers>((answers, candidate) => {
    if (!candidate || typeof candidate !== 'object' || Array.isArray(candidate)) return answers;

    Object.entries(candidate).forEach(([questionId, answer]) => {
      if (typeof questionId === 'string' && questionId && typeof answer === 'string' && answer) {
        answers[questionId] = answer;
      }
    });
    return answers;
  }, {});
}

export function sanitizeGuestQuestionAnswers(
  questions: GuestQuestion[],
  value: unknown,
  options: {
    existingAnswers?: unknown;
    requireRequiredAnswers?: boolean;
  } = {}
): { answers: GuestQuestionAnswers; errors: Record<string, string> } {
  const activeQuestions = getActiveGuestQuestions(questions);
  const submittedAnswers = normalizeEventQuestionAnswers(value);
  const existingAnswers = normalizeEventQuestionAnswers(options.existingAnswers);
  const answers: GuestQuestionAnswers = { ...existingAnswers };
  const errors: Record<string, string> = {};
  const configuredQuestions = new Map(
    (Array.isArray(questions) ? questions : [])
      .filter((question) => question && typeof question.id === 'string' && question.id)
      .map((question) => [question.id, question])
  );

  Object.entries(submittedAnswers).forEach(([questionId, answer]) => {
    const question = configuredQuestions.get(questionId);
    if (!question) {
      errors[questionId] = 'This question does not belong to this event.';
      return;
    }
    if (question.active === false) {
      if (existingAnswers[questionId]?.trim() !== answer.trim()) {
        errors[questionId] = 'This question is no longer available.';
      }
    }
  });

  activeQuestions.forEach((question) => {
    delete answers[question.id];
    const submittedAnswer = submittedAnswers[question.id]?.trim();

    if (!submittedAnswer) {
      if (question.required && options.requireRequiredAnswers !== false) {
        errors[question.id] = 'Please answer this question.';
      }
      return;
    }

    if (!isValidGuestQuestionAnswer(question, submittedAnswer)) {
      errors[question.id] = 'Please provide a valid answer.';
      return;
    }

    answers[question.id] = submittedAnswer;
  });

  return { answers, errors };
}

export function isValidGuestQuestionAnswer(question: GuestQuestion, answer?: string): boolean {
  const normalizedAnswer = answer?.trim();
  if (!normalizedAnswer) return false;

  if (question.type === 'single_choice') {
    return getActiveGuestQuestionOptions(question).some((option) => option.id === normalizedAnswer);
  }

  return normalizedAnswer.length <= 200;
}

export function validateGuestQuestionAnswers(
  questions: GuestQuestion[],
  answers: GuestQuestionAnswers
): Record<string, string> {
  return questions.reduce<Record<string, string>>((errors, question) => {
    if (question.required && !isValidGuestQuestionAnswer(question, answers[question.id])) {
      errors[question.id] = 'Please answer this question.';
    }
    return errors;
  }, {});
}
